import React from 'react';
import { useLocation } from '@reach/router';
import Link from "../utils/link";

// Used when Sanity has no shop navigation configured
const FALLBACK_NAVIGATION = [
  {
    _key: 'furniture',
    title: 'Furniture',
    children: [
      { _key: 'seating', title: 'Seating & Ottomans', link: '/shop/seating/' },
      { _key: 'cabinets-tables', title: 'Cabinets & Tables', link: '/shop/cabinets-tables/' },
      { _key: 'lighting', title: 'Lighting', link: '/shop/lighting/' },
    ],
  },
  { _key: 'rugs', title: 'Rugs', link: '/shop/rugs/' },
  { _key: 'art', title: 'Art', link: '/shop/art/' },
  {
    _key: 'fabric',
    title: 'Fabric',
    children: [
      { _key: 'fabric-all', title: 'All', link: '/shop/fabric/' },
      { _key: 'pattern', title: 'Pattern', link: '/shop/pattern/' },
      { _key: 'plain', title: 'Plain', link: '/shop/plain/' },
    ],
  },
  {
    _key: 'accessories',
    title: 'Accessories',
    children: [
      { _key: 'cushions', title: 'Cushions', link: '/shop/cushions/' },
      { _key: 'book', title: 'Book', link: '/shop/book/' },
    ],
  },
];

const toItemKey = title => title.toLowerCase().replace(/\s+/g, '-');

const CollectionSubmenu = ({ handleLinkClickHandler, activeSubMenu, toggleSubMenu, navigation = [] }) => {
  const location = useLocation();

  const normalizePath = path => {
    if (!path) return '';
    return path.endsWith('/') ? path : `${path}/`;
  };

  const isCurrentPath = path => normalizePath(path) === normalizePath(location.pathname);
  const isActive = path => isCurrentPath(path) ? 'child-active' : '';

  const isMobileMenu = () => (
    typeof window !== 'undefined' &&
    window.matchMedia('(max-width: 899px)').matches
  );

  const getFirstChildLink = (children = []) => {
    const firstLinkedChild = children.find(child => child.hasLink !== false && child.link);
    return firstLinkedChild?.link || null;
  };

  const handleNavClick = (event, link) => {
    if (!link || link === '#') return;

    event.preventDefault();
    handleLinkClickHandler(normalizePath(link));
  };

  const handleParentButtonClick = (parentId, children = []) => {
    const firstChildLink = getFirstChildLink(children);

    if (isMobileMenu() && firstChildLink) {
      handleLinkClickHandler(normalizePath(firstChildLink));
      return;
    }

    toggleSubMenu(parentId);
  };
  
  // Don't filter navigation items - show all parent items in this submenu
  const shopNavItems = navigation?.length ? navigation : FALLBACK_NAVIGATION;

  const activeItem = activeSubMenu
    ? shopNavItems.find(item => toItemKey(item.title) === activeSubMenu)
    : null;
  const activeChildren = activeItem?.children || [];
  const activeItemHasChildren = activeChildren.length > 0;

  const renderChildItems = (children = []) => children.map((child) => (
    <li key={child._key} className={isActive(child.link)}>
      {isCurrentPath(child.link) ? (
        // Use button for current page to avoid transitions
        <button className="nav-button">
          {child.title}
        </button>
      ) : (
        // Keep submenu state aligned while Gatsby changes route.
        <Link to={child.link || '#'} onClick={(event) => handleNavClick(event, child.link || '#')}>
          {child.title}
        </Link>
      )}
    </li>
  ));

  const renderChildren = (children = [], parentId) => {
    if (!children || children.length === 0) return null;
    
    return (
      <ul className={`subnav__inner--child child ${activeSubMenu === parentId ? 'active' : 'inactive'}`}>
        {renderChildItems(children)}
      </ul>
    );
  };

  return (
    <div className='submenu-container shop'>
      <ul className={`subnav__inner ${activeItemHasChildren ? 'active' : ''}`}>
        {shopNavItems.map((navItem) => {
          // Create a key for this item to use with activeSubMenu
          const itemKey = toItemKey(navItem.title);
          
          // Check if this item has children
          const hasChildren = navItem.children && navItem.children.length > 0;
          
          // We need to hide all items except the active one when:
          // 1. An item with children is active, AND
          // 2. This is not that active item
          const shouldHide = activeItemHasChildren && activeSubMenu !== itemKey;
          
          // Create the appropriate class for this item
          let itemClass = 'parent';
          if (activeSubMenu === itemKey) {
            itemClass += ' active';
          } else if (shouldHide) {
            itemClass += ' inactive hide'; // Add an extra 'hide' class
          }
          
          return (
            <li 
              key={navItem._key} 
              className={itemClass}
            >
              {hasChildren ? (
                <>
                  <button onClick={() => handleParentButtonClick(itemKey, navItem.children)}>
                    {navItem.title}
                  </button>
                  {renderChildren(navItem.children, itemKey)}
                </>
              ) : (
                <>
                  {/* Check if this is the current page */}
                  {isCurrentPath(navItem.link) ? (
                    // Use a button instead of Link for current page
                    <button
                      // Re-select only; nothing to close, and toggling would drop its highlight
                      onClick={() => activeSubMenu !== itemKey && toggleSubMenu(itemKey)}
                      className="nav-button"
                    >
                      {navItem.title}
                    </button>
                  ) : (
                    // Keep submenu state aligned while Gatsby changes route.
                    <Link to={navItem.link || '#'} onClick={(event) => handleNavClick(event, navItem.link || '#')}>
                      {navItem.title}
                    </Link>
                  )}
                </>
              )}
            </li>
          );
        })}
      </ul>

      {/* Mobile only: the active item's children get their own scrolling row.
          Always rendered so the header height doesn't jump between pages. */}
      <ul className='subnav__mobile-children'>
        {renderChildItems(activeChildren)}
      </ul>
    </div>
  );
};

export default CollectionSubmenu;
